import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import { LanguageSwitcher, useI18n } from "../i18n";
import { BrandMark, IconClose, IconMenu } from "./icons";

export interface ShellNavItem {
  path: string;
  /** i18n key for the label. */
  label: string;
  icon: ComponentType<{ size?: number }>;
  /** Match this path exactly (the root route, which every path starts with). */
  end?: boolean;
}

export interface ShellNavGroup {
  /** i18n key for the small caps heading above the group, if it has one. */
  label?: string;
  items: ShellNavItem[];
}

/**
 * The chrome both consoles wear: a sidebar that names where you can go, a
 * glass topbar that names where you are, and the page below it.
 *
 * One component rather than two nearly-identical ones, because the customer
 * dashboard and the admin console are meant to *be* the same product — the
 * only differences are the nav, the wordmark, and what sits in the footer.
 *
 * `banner` is the one thing that outranks the page: it rides in the topbar,
 * which is sticky, so what it says stays on screen wherever the reader has
 * scrolled to. Today it says "this console is not yours" (B2B §7.1).
 *
 * Below 900 px the sidebar stops being a column and becomes a drawer the
 * topbar's button pulls in; it closes itself on navigation, so a tap on a
 * link never leaves the drawer covering the page it just opened.
 */
export function Shell({
  brandName,
  brandSub,
  groups,
  banner,
  footer,
  children,
}: {
  brandName: string;
  brandSub?: string;
  groups: ShellNavGroup[];
  /** Kept beside the breadcrumb rather than above the page: a warning that
   *  scrolls away is a warning for the first screenful only. */
  banner?: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setMenuOpen(false), [pathname]);

  // Which nav entry the current URL belongs to. Longest match wins, so
  // /devices/abc still lights Devices rather than falling back to the root.
  const items = groups.flatMap((group) => group.items);
  const current =
    items
      .filter((item) => pathname === item.path || (!item.end && pathname.startsWith(`${item.path}/`)))
      .sort((a, b) => b.path.length - a.path.length)[0] ??
    items.find((item) => item.path === "/");

  return (
    <div className="shell">
      {menuOpen && (
        <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
      )}

      <aside className={menuOpen ? "sidebar open" : "sidebar"}>
        <div className="brand">
          <BrandMark />
          <span className="brand-text">
            <span className="brand-name">{brandName}</span>
            {brandSub && <span className="brand-sub">{brandSub}</span>}
          </span>
        </div>

        <nav className="nav">
          {groups.map((group, index) => (
            <div key={group.label ?? index} className="nav-group">
              {group.label && <p className="nav-label">{t(group.label)}</p>}
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) => (isActive ? "nav-item active" : "nav-item")}
                >
                  <item.icon size={17} />
                  <span>{t(item.label)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="spacer" />
        <div className="sidebar-foot">{footer}</div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn nav-toggle"
            aria-label={t(menuOpen ? "app.closeMenu" : "app.menu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <IconClose /> : <IconMenu />}
          </button>
          <div className="crumbs">
            <span>{brandName}</span>
            {current && (
              <>
                <span className="sep">/</span>
                <strong>{t(current.label)}</strong>
              </>
            )}
          </div>
          {banner}
          <div className="spacer" />
          <LanguageSwitcher />
        </header>

        <main className="content">{children}</main>
      </div>
    </div>
  );
}
